<template>
  <div>
    <div v-if="impersonateData.is_impersonated" class="bg-yellow p-2">
      <div class="mx-auto" style="width:800px">
        <div class="flex justify-between items-center">
          <div class="text-lg">
            You are currently logged in as
            <span class="text-primary font-bold">{{ impersonateData.email }}</span>
          </div>
          <gd-button
            primary
            class="px-3"
            size="auto"
            @click="
              backToMainAccount(
                impersonateData.existing_email,
                impersonateData.refresh_token
              )
            "
            >Back To Main Account</gd-button
          >
        </div>
      </div>
    </div>
    <blocked-user-modal v-if="showBlockedUserModal" :blockedUserCode="blockedUserCode" @close="showBlockedUserModal = false"/>
    <div v-else class="h-screen flex overflow-hidden bg-grey-100">
      <loading-overlay
        :is-loading="showLoader"
        :full-page="true"
      />
      <phone-prompt ref="phonePrompt" @submit="addUserPhone" />
      <browser-banner />
      <promotion-banner />
      <slot name="sidebar"></slot>
      <div class="flex flex-col w-0 flex-1 overflow-hidden">
        <!-- Header -->
        <div
          v-if="user"
          class="relative z-10 bg-grey-50 shadow main-header px-5"
          ref="gpHeader"
        >
          <div class="h-16 flex-shrink-0 flex md:container mx-auto">
            <div class="flex-1 flex justify-between">
              <div class="flex flex-grow items-center">
                <slot name="header-bar"></slot>
              </div>

              <div class="flex items-center">
                <div
                  v-if="showHeaderIcon"
                  class="
                  w-13
                  h-13
                  flex
                  items-center
                  justify-center
                  gap-2
                  text-grey-500
                  select-none
                  relative
                "
                >
                  <support-navbar />
                  <button
                    class="cursor-pointer focus:outline-none notification-expander"
                  >
                    <notify-icon
                      class="hover:text-primary-dark active:text-primary-dark"
                    ></notify-icon>
                  </button>
                </div>

                <div
                  class="
                  w-13
                  h-13
                  flex
                  items-center
                  justify-center
                  text-grey-500
                  select-none
                  relative
                "
                >
                  <!-- Dashboard App Switcher -->
                  <button
                    class="cursor-pointer focus:outline-none"
                    @click="showApp = !showApp"
                  >
                    <dashboard-app-switcher-icon />
                  </button>

                  <transition name="slideup" mode="out-in">
                    <div
                      v-if="showApp"
                      v-on-click-outside="() => (this.showApp = false)"
                      class="
                        bg-white
                        rounded-lg
                        shadow-md
                        absolute
                        right-0
                        top-full
                        z-50
                        w-100
                        border-solid border border-grey-200
                        shadow
                        overflow-scroll
                      "
                      style="max-height: calc(100vh - 4rem); max-width: 80vw;"
                    >
                      <div class="mt-2 p-4 bg-white">
                        <a
                          v-for="(app, index) in apps"
                          :key="index"
                          :href="app.url"
                          class="
                          -m-3
                          p-3
                          flex
                          rounded-lg
                          hover:bg-grey-100
                          transition
                          ease-in-out
                          duration-150
                        "
                          :target="app.target"
                          @click="handleRedirect($event, app.url, app.target)"
                        >
                          <div
                            class="
                            flex flex-col
                            items-center
                            justify-center
                            border border-grey-100
                            rounded
                            h-10
                            w-10
                            min-w-10
                            shadow
                          "
                            v-if="app.logo"
                          >
                            <img
                              :src="app.logo"
                              class="flex-shrink-0 h-6 w-6 min-w-5"
                            />
                          </div>
                          <div
                            class="flex flex-col"
                            :class="{ 'ml-3': app.logo }"
                          >
                            <p class="text-sm font-semibold text-grey-700">
                              {{ app.title }}
                            </p>
                            <p class="text-xs text-grey-600">
                              {{ app.description }}
                            </p>
                          </div>
                        </a>
                      </div>
                    </div>
                  </transition>
                </div>

                <!-- Profile dropdown -->
                <div class="ml-3 relative">
                  <div>
                    <button
                      type="button"
                      class="
                      max-w-xs
                      bg-white
                      flex
                      items-center
                      text-sm
                      rounded-full
                      focus:outline-none
                      focus:ring-2
                      focus:ring-offset-2
                      focus:ring-indigo-500
                    "
                      id="user-menu-button"
                      aria-expanded="false"
                      aria-haspopup="true"
                      @click="toggleMenu()"
                    >
                      <span class="sr-only">{{
                        $t('frontend_gduikit_open_user_menu')
                      }}</span>
                      <img
                        v-if="profilePicture"
                        class="h-8 w-8 rounded-full"
                        :src="profilePicture"
                        alt=""
                      />
                      <img
                        v-else
                        class="h-8 w-8 rounded-full"
                        src="../assets/user.png"
                        alt=""
                      />
                    </button>
                    <transition name="fade" mode="out-in">
                      <div
                        v-if="menuOpen"
                        v-on-click-outside="closeMenu"
                        class="
                        absolute
                        w-64
                        bg-white
                        right-0
                        top-0
                        mt-13
                        rounded-lg
                        z-40
                        border-solid border border-grey-200
                        shadow
                      "
                      >
                        <div class="px-4 py-2">
                          <div
                            :key="`item.text-${index}`"
                            @click="callMenuAction(item)"
                            class="cursor-pointer text-grey-700 mb-2 text-sm"
                            v-for="(item, index) in menu"
                          >
                            <hr
                              v-if="item.text === 'ruler'"
                              class="border-t-1 border-grey-100"
                            />
                            <span
                              v-else
                              :id="item.text === 'Logout' ? 'Logout' : ''"
                            >
                              {{ item.text }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
  
          <main
            :class="
              `${
                this.target === 'pages'
                  ? 'flex w-full h-full'
                  : 'flex-1 relative overflow-y-auto focus:outline-none'
              }`
            "
            :style="
              `max-height: calc(100% - ${
                this.target === 'pages' ? gpHeaderHeight : '4rem'
              }px);`
            "
          >
            <div class="flex flex-row w-full h-full">
              <slot name="content"></slot>
            </div>
          </main>
  
          <dashboard-setting ref="dashboardSetting" />
          <promotion-modal ref="promotionModal" />
          <ads-modal ref="adsModal" />
  
          <account-modal
            :open="editingUserDetails"
            @close="closeAccountModal"
            ref="accountModal"
          />
        </div>
      </div>
    </div>
</template>

<script>
import api from '@groovepages/gd-lib/api';
import Auth from '@groovepages/gd-lib/auth';
import Notify from '../notify';
import axios from 'axios';
import storage from 'local-storage-fallback';
import publisher from 'publisher';
import AppSwitcherIcon from '../assets/app-switcher-icon.svg';
import DashboardAppSwitcherIcon from '../assets/dashboard-app-switcher.svg';
import NotifyIcon from '../assets/notification-icon.svg';
import directives from '../directives';
import AccountModal from './AccountModal';
import AdsModal from './AdsModal';
import BlockedUserModal from './BlockedUserModal';
import BrowserBanner from './BrowserBanner';
import GdButton from './Button';
import DashboardSetting from './DashboardSetting';
import LoadingOverlay from './LoadingOverlay';
import GdModal from './Modal';
import PhonePrompt from './PhonePrompt';
import PromotionBanner from './PromotionBanner';
import PromotionModal from './PromotionModal';
import SupportNavbar from './SupportNavbar.vue';


export default {
  name: 'BaseLayout',
  components: {
    PhonePrompt,
    GdButton,
    NotifyIcon,
    AppSwitcherIcon,
    DashboardAppSwitcherIcon,
    AccountModal,
    BrowserBanner,
    AdsModal,
    DashboardSetting,
    PromotionModal,
    PromotionBanner,
    GdModal,
    LoadingOverlay,
    BlockedUserModal,
    SupportNavbar
  },
  directives,
  props: ['noHelpBar', 'origin', 'target'],
  data() {
    return {
      showBlockedUserModal: false,
      blockedUserCode: 0,
      showApp: false,
      menuOpen: false,
      showLoader: false,
      editingUserDetails: false,
      langs: ['en', 'es'],
      gpHeaderHeight: 52,
      notificationPopOpen: false,
      hasUnreadNotification: false,
      changelog: {},
      impersonateData: {},
      showHeaderIcon: false
    };
  },
  watch: {
    $route(to, from) {
      this.checkWindowUrl();
    }
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    menu() {
      const items = [
        ...(typeof this.$store.state.builder !== 'undefined' &&
        (this.$store.state.builder.mode === 'page-template' ||
          this.$store.state.builder.mode === 'site-template')
          ? [
              {
                text: this.$t('frontend_builder_template_settings'),
                callback: () => {
                  publisher.publish('openPublishSettings');
                  this.$root.$emit('openPublishSettings');
                }
              },
              {
                text: 'ruler'
              }
            ]
          : []),
        {
          text: this.$t('frontend_baselayout_settings_billing'),
          callback: () => this.editUserDetails()
        },
        this.$store.state &&
        this.$store.state.user &&
        this.$store.state.user.admin &&
        !this.$store.state.user.groovemailadmin
          ? {
              text: this.$t('frontend_baselayout_my_ads'),
              callback: () => {
                this.$refs.adsModal.open();
              }
            }
          : [],
        this.$store.state &&
        this.$store.state.user &&
        this.$store.state.user.admin &&
        !this.$store.state.user.groovemailadmin
          ? {
              text: this.$t('frontend_baselayout_dashboard_settings'),
              callback: () => {
                this.$refs.dashboardSetting.open();
              }
            }
          : [],
        this.$store.state &&
        this.$store.state.user &&
        this.$store.state.user.admin &&
        !this.$store.state.user.groovemailadmin
          ? {
              text: this.$t('frontend_baselayout_my_promotions'),
              callback: () => {
                this.$refs.promotionModal.open();
              }
            }
          : [],
        this.$store.state &&
        this.$store.state.user &&
        this.$store.state.user.impersonateadmin
          ? {
              text: this.$t('frontend_baselayout_user_manage'),
              callback: () => {
                window.open(`${window.config.grooveappsurl}/grooveadmin/user`);
              }
            }
          : [],
        {
          text: this.$t('frontend_baselayout_groove_partner_program'),
          callback: () => {
            window.open(
              'https://app.groove.cm/static/partner-program',
              '_blank'
            );
          }
        },
        {
          text: 'ruler'
        }
      ];
      items.push({
        text: this.$t('frontend_logout'),
        callback: this.logout
      });
      return items;
    },
    showMenu() {
      return this.user;
    },
    userObject() {
      return JSON.parse(localStorage.getItem('user')) ?? {};
    },
    profilePicture() {
      const { metadata } = this.userObject;

      if (
        metadata &&
        metadata.hasOwnProperty('profile_picture') &&
        metadata.profile_picture != ''
      ) {
        return metadata.profile_picture;
      }

      return false;
    },
    issetPhoneNumber() {
      const { metadata } = this.userObject;

      if (this.userId == null) return true;
      if (
        metadata &&
        metadata.hasOwnProperty('contact_details') &&
        metadata.contact_details.hasOwnProperty('phone') &&
        metadata.contact_details.phone
      )
        return true;

      return false;
    },
    userId() {
      if (this.userObject.hasOwnProperty('id')) return this.userObject.id;

      return null;
    },
    brandData() {
      return this.$store.getters.brandData;
    },
    apps() {
      if (!this.brandData) return [];
      return this.brandData.apps.filter(app => app.type.includes('apps'));
    }
  },
  async created() {
    const result = await api.get(
      `${window.gdEnv.VUE_APP_API_URL}/groovedigital/user-status`
    );

    if (!result.blocked){
      return;
    }

    if (this.$route.path === '/login'){
      return;
    }

    if (result.blocked === 3 && this.$route?.matched[0]?.components?.default?.layout == 'defaultForAffiliates') {
      // Free users have access only to the affiliate area
      return;
    }

    this.showBlockedUserModal = true;
    this.blockedUserCode = result.blocked;
  },
  mounted() {
    if (this.userObject.is_legacy && !this.issetPhoneNumber)
      this.$refs.phonePrompt.open(); // Ask for phone number only for old users

    this.gpHeaderHeight = this.$refs?.gpHeader?.offsetHeight;
    this.$root.$on('open-account-modal', this.editUserDetails);
    this.impersonateData = this.$store.getters.user;
    const li_sidebar = {
      workspace_id: 'f634edf5-70e7-429d-8c94-c26dfaeaee26'
    };
    if (localStorage.getItem('user')) {
      const user = JSON.parse(localStorage.getItem('user'));
      li_sidebar.name = user.name;
      li_sidebar.email = user.email;
    }
    window.li_sidebar = li_sidebar;
    const recaptchaScript = document.createElement('script');
    recaptchaScript.setAttribute(
      'src',
      'https://cdn.loopedin.io/js/sidebar.min.js?v=0.1'
    );
    recaptchaScript.setAttribute('defer', 'defer');
    document.head.appendChild(recaptchaScript);
    this.checkWindowUrl();
  },
  methods: {
    async addUserPhone(phone) {
      let { metadata } = this.userObject;

      if (metadata.hasOwnProperty('contact_details'))
        metadata.contact_details.phone = phone;
      else
        metadata = {
          ...metadata,
          ...{
            contact_details: {
              phone
            }
          }
        };

      await api
        .post(
          `${window.gdEnv.VUE_APP_API_URL}/groovedigital/auth/users/update-meta/${this.userId}`,
          metadata
        )
        .then(result => {
          Auth.updateMeta(result);
          this.$refs.phonePrompt.close();
        })
        .catch(err => {
          Notify.fail(err);
        });
    },
    backToMainAccount(email, refresh_token) {
      this.showLoader = true;
      const userData = {
        email,
        refresh_token
      };
      api
        .post(
          `${window.gdEnv.VUE_APP_API_URL}/grooveadmin/reverseimpersonate`,
          userData
        )
        .then(response => {
          const adminData = Auth.authenticatedUser();
          storage.setItem('adminuser', JSON.stringify(adminData));
          Auth.setUserData(response, true);
          this.$store.commit('login', response);
          window.location.replace(
            `${window.config.grooveappsurl}/${localStorage.getItem(
              'app_name'
            )}${localStorage.getItem('app_source')}`
          );
        })
        .catch(err => {
          this.showLoader = false;
          Notify.fail(err);
          Auth.logout();
          window.location = localStorage.getItem('app_name');
        });
    },
    editUserDetails(section) {
      this.editingUserDetails = true;
      this.$nextTick(() => {
        if (section) {
          this.$refs.accountModal.$refs.tabs.openTab(section);
        }
      });
    },
    editTeamMembers() {
      this.editingTeamMembers = true;
    },
    closeTeamMembersModal() {
      this.editingTeamMembers = false;
    },
    myBillings() {
      window.location.replace('/billing');
    },
    closeAccountModal() {
      this.editingUserDetails = false;
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    callMenuAction(menuItem) {
      this.menuOpen = false;
      menuItem.callback();
    },
    logout() {
      storage.removeItem('mailList');
      storage.removeItem('mailTag');
      Auth.logout();
      window.location.replace('/');
    },
    checkWindowUrl() {
      const windowUrl = window.location.pathname;
      if (
        windowUrl === '/' ||
        windowUrl === '/groovepages/' ||
        windowUrl === '/groovesell/' ||
        windowUrl === '/groovesell/dashboard' ||
        windowUrl === '/groovemail/' ||
        windowUrl === '/groovemail/dashboard' ||
        windowUrl === '/groovemember/' ||
        windowUrl === '/groovemember/dashboard' ||
        windowUrl === '/groovevideo/' ||
        windowUrl === '/groovevideo/dashboard' ||
        windowUrl === '/grooveblog/' ||
        windowUrl === '/grooveblog/blog' ||
        windowUrl === '/static/groovekart'
      ) {
        this.showHeaderIcon = true;
      } else {
        this.showHeaderIcon = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.tooltip {
  display: block !important;
  z-index: 10000;

  .tooltip-inner {
    background: black;
    color: white;
    border-radius: 16px;
    padding: 5px 10px 4px;
  }

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
    z-index: 1;
  }

  &[x-placement^='top'] {
    margin-bottom: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^='bottom'] {
    margin-top: 5px;

    .tooltip-arrow {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^='right'] {
    margin-left: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[x-placement^='left'] {
    margin-right: 5px;

    .tooltip-arrow {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &.popover {
    $color: #f9f9f9;

    .popover-inner {
      background: $color;
      color: black;
      padding: 24px;
      border-radius: 5px;
      box-shadow: 0 5px 30px rgba(black, 0.1);
    }

    .popover-arrow {
      border-color: $color;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s, visibility 0.15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.15s;
  }
}
</style>

<style lang="scss">
.main-header
  .flex-1
  > .flex-grow
  > .flex-row
  .vue-portal-target:first-child:empty {
  margin-right: -16px;
}
@media only screen and (min-width: 1770px) and (max-width: 2099px) {
  .container {
    max-width: 1400px !important;
  }
}
@media only screen and (min-width: 2100px) {
  .container {
    max-width: 1600px !important;
  }
}
</style>
